import React, { useState, useRef } from 'react';
import { Animated } from 'react-native';
import i18n from 'i18n-js';

import { convertTimeLetter, formatTimeEvent, Popup } from '@helpers';
import { useAppDispatch } from '@store/hooks/useAppDispatch';
import { edit } from '@reducers/clampingTools';

import ClampingToolDetails from '@components/clampingTool/components/clampingToolDetails';

import type { WithColor } from '@styles/colors';
import type { ClampingTool } from '@reducers/clampingTools';

interface UseManageToolsMobileParams extends WithColor {
  clampingTools: {
    clampingTools: ClampingTool[];
    loaded: boolean;
  };
  refetchTools?: () => void;
  route?: { params?: Record<string, any> };
}

export const useManageToolsMobile = ({ clampingTools: { clampingTools }, refetchTools, route }: UseManageToolsMobileParams) => {
  const params = route?.params;
  const dispatch = useAppDispatch();

  const fadeAnimsRef = useRef<Record<string, Animated.Value>>({});

  clampingTools.forEach((item) => {
    if (!fadeAnimsRef.current[item.clampingToolId]) {
      fadeAnimsRef.current[item.clampingToolId] = new Animated.Value(0);
    }
  });

  const [showEdit, setShowEdit] = useState(false);
  const [selectedTool, setSelectedTool] = useState<Record<string, any>>({});
  const [editConfig, setEditConfig] = useState<Record<string, any>>({});

  const toggleEdit = () => {
    setShowEdit((prev) => !prev);
  };

  const toggleAdd = () => {
    Popup({
      Component: (p) => <ClampingToolDetails {...{ ...p, params, newTool: true }} />,
    });
  };

  const getItemTime = (item: ClampingTool) => {
    return item.machiningDuration > 0 ? formatTimeEvent(convertTimeLetter(item.machiningDuration)) : '0' + i18n.t('s');
  };

  const getItemDetails = (item: ClampingTool) => [
    {
      name: 'Description',
      value: item?.toolDescription,
      valueName: 'toolDescription',
    },
    {
      name: 'Stickout',
      value: `${item?.toolStickoutMM ?? 0} ${i18n.t('mm')}`,
      keyboardType: 'number-pad',
      valueName: 'toolStickoutMM',
    },
    {
      name: 'More',
      value: '',
      textColor: 'Link/Light/Primary',
    },
  ];

  const onItemPress = (item: ClampingTool) => {
    Animated.timing(fadeAnimsRef.current?.[item.clampingToolId], {
      toValue: fadeAnimsRef.current?.[item.clampingToolId]?._value === 0 ? 1 : 0,
      duration: 400,
      useNativeDriver: false,
    }).start();
  };

  const onLineClick = (line: Record<string, any>, item: ClampingTool) => {
    if (line.name === 'More') {
      Popup({
        Component: (p) => <ClampingToolDetails {...{ ...p, clampingTool: item, params, newTool: false }} />,
      });
      return;
    }
    setSelectedTool(item);
    setEditConfig({
      header: line.name,
      initData: {
        [line.valueName]: item[line.valueName] ? String(item[line.valueName]) : '',
      },
      inputs: [
        {
          key: line.valueName,
          isLable: false,
          noArrow: true,
          placeholder: line.name,
          keyboardType: line.keyboardType,
        },
      ],
    });
    setShowEdit(true);
  };

  const onEditSubmit = async (data: Record<string, any>, onClose: () => void) => {
    if (data.toolStickoutMM) {
      data.stickoutMM = parseInt(data.toolStickoutMM, 10);
    }
    if (data.toolDescription) {
      data.description = data.toolDescription;
    }
    await dispatch(edit({
      toolId: selectedTool.toolId,
      toolName: selectedTool.toolName,
      description: selectedTool.toolDescription,
      stickoutMM: selectedTool.toolStickoutMM,
      ...data,
    }));
    refetchTools?.();
    onClose();
  };

  return {
    showEdit,
    editConfig,
    fadeAnimsRef,
    toggleEdit,
    toggleAdd,
    getItemTime,
    getItemDetails,
    onItemPress,
    onLineClick,
    onEditSubmit,
  };
};
